import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getPageCatalogEntry,
  MORNING_MEETING_SECTION_KEY,
  MORNING_MEETING_SECTION_NAME,
} from '@classy/shared';
import { useApi } from '../api/ApiProvider.js';
import type { ClassroomLayoutDto } from '../api/types.js';
import { CLASSROOM_PAGE_MODULES, type ClassroomPageKey } from '../classroomPages/registry.js';

const REGISTRY_KEYS = new Set<string>(CLASSROOM_PAGE_MODULES.map((m) => m.key));

/**
 * Flatten a server layout into the ordered enabled-page list the existing UI
 * works with: Home is pinned (always first when it has a module), then the
 * Morning Meeting section's pages, then the standalone class-level pages.
 * Filtered to keys that actually have a registered module (so a catalog key
 * like `links` is ignored until its module ships).
 */
function flatten(layout: ClassroomLayoutDto | undefined): ClassroomPageKey[] {
  if (!layout) return [];
  const ordered = [...layout.sections.flatMap((s) => s.pages), ...layout.standalone].filter((k) =>
    REGISTRY_KEYS.has(k),
  );
  const withoutHome = ordered.filter((k) => k !== 'home');
  const result = REGISTRY_KEYS.has('home') ? ['home', ...withoutHome] : withoutHome;
  return result as ClassroomPageKey[];
}

/**
 * Split a flat enabled list back into the persisted layout shape by catalog
 * placement. Pinned pages (home/search) are dropped — they're implicit, not
 * part of the customizable layout.
 */
function toLayoutBody(pages: readonly ClassroomPageKey[]): {
  sections: { key: string; name: string; pages: string[] }[];
  standalone: string[];
} {
  const mm: string[] = [];
  const standalone: string[] = [];
  for (const k of pages) {
    const placement = getPageCatalogEntry(k)?.placement ?? 'morning-meeting';
    if (placement === 'pinned') continue;
    if (placement === 'standalone') standalone.push(k);
    else mm.push(k);
  }
  return {
    sections: [{ key: MORNING_MEETING_SECTION_KEY, name: MORNING_MEETING_SECTION_NAME, pages: mm }],
    standalone,
  };
}

/**
 * Server-backed replacement for the per-device `enabledStorage` list. Exposes
 * the same flat `enabledPages` get/set the settings tab strip + live nav
 * already use, so swapping the storage backend is behavior-preserving while the
 * layout now follows the teacher across devices. Writes are optimistic so every
 * consumer (settings strip, live nav) updates instantly.
 */
export function useClassroomPages(classroomId: string | null | undefined) {
  const api = useApi();
  const qc = useQueryClient();
  const id = classroomId ?? 'none';
  const queryKey = ['classroom-layout', id] as const;

  const query = useQuery({
    queryKey,
    queryFn: () => api.layout.get(classroomId as string),
    enabled: !!classroomId,
  });

  const mutation = useMutation({
    mutationFn: (pages: ClassroomPageKey[]) =>
      api.layout.put(classroomId as string, toLayoutBody(pages)),
    onSuccess: (next) => qc.setQueryData(['classroom-layout', id], next),
  });

  const setEnabledPages = useCallback(
    (next: ClassroomPageKey[]) => {
      if (!classroomId) return;
      const k = ['classroom-layout', classroomId] as const;
      const body = toLayoutBody(next);
      const prev = qc.getQueryData<ClassroomLayoutDto>(k);
      // Optimistically reflect the change so the strip + live nav update now.
      qc.setQueryData<ClassroomLayoutDto>(k, (old) =>
        old
          ? { ...old, sections: body.sections, standalone: body.standalone }
          : {
              classroomId,
              sections: body.sections,
              standalone: body.standalone,
              updatedAt: null,
            },
      );
      mutation.mutate(next, {
        onError: () => {
          if (prev) qc.setQueryData(k, prev);
        },
      });
    },
    [classroomId, mutation, qc],
  );

  return {
    enabledPages: flatten(query.data),
    setEnabledPages,
    isLoading: query.isLoading,
  };
}
